import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ChevronRight, AlertTriangle, Archive, ArchiveRestore, Trash2, Users } from 'lucide-react';
import { api, errMsg } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import CoachShell from '@/components/CoachShell';
import { LoadingScreen } from '@/components/State';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';

const RISK = {
  new: { label: 'risk.new', cls: 'border-blue-400/40 bg-blue-400/10 text-blue-400', dot: 'bg-blue-400' },
  active: { label: 'risk.active', cls: 'border-asgym-accent/40 bg-asgym-accent/10 text-asgym-accent', dot: 'bg-asgym-accent' },
  at_risk: { label: 'risk.atRisk', cls: 'border-asgym-warn/40 bg-asgym-warn/10 text-asgym-warn', dot: 'bg-asgym-warn' },
  inactive: { label: 'risk.inactive', cls: 'border-asgym-error/40 bg-asgym-error/10 text-asgym-error', dot: 'bg-asgym-error' },
};

export default function Clients() {
  const nav = useNavigate();
  const { t } = useI18n();
  const [clients, setClients] = useState([]);
  const [archived, setArchived] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [view, setView] = useState('active'); // 'active' | 'archived'
  const [busyId, setBusyId] = useState(null);

  // delete dialog state
  const [delTarget, setDelTarget] = useState(null);
  const [confirmName, setConfirmName] = useState('');
  const [deleting, setDeleting] = useState(false);

  const loadActive = async () => {
    const res = await api.get('/coach/clients-overview');
    setClients(res.data?.clients || res.data || []);
  };
  const loadArchived = async () => {
    const res = await api.get('/coach/clients/archived');
    setArchived(res.data || []);
  };

  useEffect(() => {
    (async () => {
      try { await Promise.all([loadActive(), loadArchived()]); }
      catch (e) { toast.error(errMsg(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const norm = (s) => (s === 'atRisk' ? 'at_risk' : s);

  const source = view === 'active' ? clients : archived;
  const filtered = source.filter((c) => {
    const st = norm(c.risk?.status);
    const okFilter = view === 'archived' || filter === 'all' || st === filter;
    const name = `${c.first_name || ''} ${c.last_name || ''} ${c.email || ''}`.toLowerCase();
    return okFilter && name.includes(q.toLowerCase());
  });

  const stats = {
    total: clients.length,
    active: clients.filter((c) => norm(c.risk?.status) === 'active').length,
    archived: archived.length,
  };

  const fullName = (c) => `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email || '';

  const handleArchive = async (c) => {
    setBusyId(c.id);
    try {
      await api.post(`/clients/${c.id}/archive`);
      toast.success(t('coach.archivedToast'));
      await Promise.all([loadActive(), loadArchived()]);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusyId(null); }
  };

  const handleRestore = async (c) => {
    setBusyId(c.id);
    try {
      await api.post(`/clients/${c.id}/restore`);
      toast.success(t('coach.restoredToast'));
      await Promise.all([loadActive(), loadArchived()]);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusyId(null); }
  };

  const openDelete = (c) => { setDelTarget(c); setConfirmName(''); };

  const confirmMatches = delTarget && confirmName.trim() === fullName(delTarget);

  const handleDelete = async () => {
    if (!delTarget || !confirmMatches) return;
    setDeleting(true);
    try {
      await api.delete(`/clients/${delTarget.id}`);
      toast.success(t('coach.deletedToast'));
      setDelTarget(null);
      setConfirmName('');
      await Promise.all([loadActive(), loadArchived()]);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setDeleting(false); }
  };

  return (
    <CoachShell title={t('coach.dashboard')}>
      {loading ? <LoadingScreen /> : (
        <>
          <div className="mb-6 grid grid-cols-3 gap-3">
            {[[t('coach.total'), stats.total], [t('coach.active'), stats.active], [t('coach.archived'), stats.archived]].map(([l, v], i) => (
              <div key={i} className="as-card p-4">
                <p className="as-label">{l}</p>
                <p className="mt-1 font-display text-3xl font-extrabold text-white">{v}</p>
              </div>
            ))}
          </div>

          {/* View switch: Active vs Archived */}
          <div className="mb-4 flex gap-2">
            <button onClick={() => setView('active')} data-testid="view-active"
              className={`as-chip flex items-center gap-1.5 ${view === 'active' ? 'border-asgym-accent bg-asgym-accent/15 text-asgym-accent' : 'border-white/[0.08] bg-white/[0.03] text-asgym-sub'}`}>
              <Users size={13} />{t('coach.activeClients')}
            </button>
            <button onClick={() => setView('archived')} data-testid="view-archived"
              className={`as-chip flex items-center gap-1.5 ${view === 'archived' ? 'border-asgym-accent bg-asgym-accent/15 text-asgym-accent' : 'border-white/[0.08] bg-white/[0.03] text-asgym-sub'}`}>
              <Archive size={13} />{t('coach.archivedClients')} ({stats.archived})
            </button>
          </div>

          <div className="mb-4 flex items-center gap-3">
            <div className="flex flex-1 items-center gap-2 rounded-xl border border-white/[0.06] bg-asgym-surface px-3">
              <Search size={16} className="text-asgym-muted" />
              <input className="h-11 flex-1 bg-transparent text-sm text-white placeholder:text-asgym-muted outline-none" placeholder={t('coach.list')} value={q} onChange={(e) => setQ(e.target.value)} data-testid="client-search" />
            </div>
          </div>

          {view === 'active' && (
            <div className="mb-5 flex gap-2">
              {[['all', 'risk.all'], ['new', 'risk.new'], ['active', 'risk.active'], ['at_risk', 'risk.atRisk'], ['inactive', 'risk.inactive']].map(([k, tk]) => (
                <button key={k} onClick={() => setFilter(k)} data-testid={`filter-${k}`}
                  className={`as-chip ${filter === k ? 'border-asgym-accent bg-asgym-accent/15 text-asgym-accent' : 'border-white/[0.08] bg-white/[0.03] text-asgym-sub'}`}>{t(tk)}</button>
              ))}
            </div>
          )}

          {filtered.length === 0 ? (
            <p className="py-16 text-center text-sm text-asgym-sub" data-testid="clients-empty">
              {view === 'archived' ? t('coach.noArchived') : t('coach.empty')}
            </p>
          ) : (
            <div className="space-y-2.5">
              {filtered.map((c) => {
                const st = norm(c.risk?.status) || 'active';
                const r = RISK[st] || RISK.active;
                const factors = c.risk?.factors || [];
                const isBusy = busyId === c.id;
                return (
                  <div key={c.id} data-testid={`client-card-${c.id}`}
                    className="flex w-full items-center gap-4 rounded-2xl border border-white/[0.06] bg-asgym-surface p-4 text-left transition-colors hover:bg-asgym-elevated">
                    <button onClick={() => nav(`/coach/client/${c.id}`)} data-testid={`client-open-${c.id}`}
                      className="flex min-w-0 flex-1 items-center gap-4 text-left">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/[0.04] font-display text-sm font-extrabold text-asgym-accent">
                        {(c.first_name?.[0] || '') + (c.last_name?.[0] || '')}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="truncate text-[15px] font-bold text-white">{c.first_name} {c.last_name}</p>
                          {view === 'active' ? (
                            <span className={`as-chip ${r.cls} !py-0.5`}><span className={`h-1.5 w-1.5 rounded-full ${r.dot}`} />{t(r.label)}</span>
                          ) : (
                            <span className="as-chip border-white/[0.12] bg-white/[0.04] text-asgym-muted !py-0.5"><Archive size={11} />{t('coach.archivedBadge')}</span>
                          )}
                        </div>
                        <p className="mt-0.5 flex items-center gap-3 text-[11px] text-asgym-muted">
                          <span>{t('coach.weightLabel')}: {c.current_weight || '—'}kg</span>
                          <span>{t('coach.targetLabel')}: {c.target_weight || '—'}kg</span>
                          {view === 'active' && !c.has_training_plan && !c.has_nutrition_plan && (
                            <span className="font-semibold text-asgym-warn" data-testid={`no-plan-${c.id}`}>{t('coach.noPlan')}</span>
                          )}
                          {view === 'active' && factors.length > 0 && <span className="flex items-center gap-1 text-asgym-warn"><AlertTriangle size={11} />{factors.length}</span>}
                        </p>
                      </div>
                    </button>

                    <div className="flex shrink-0 items-center gap-1">
                      {view === 'active' ? (
                        <button title={t('coach.archive')} disabled={isBusy} onClick={() => handleArchive(c)} data-testid={`archive-btn-${c.id}`}
                          className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/[0.08] bg-white/[0.03] text-asgym-sub transition-colors hover:bg-white/[0.06] hover:text-white disabled:opacity-40">
                          <Archive size={16} />
                        </button>
                      ) : (
                        <button title={t('coach.restore')} disabled={isBusy} onClick={() => handleRestore(c)} data-testid={`restore-btn-${c.id}`}
                          className="flex h-9 w-9 items-center justify-center rounded-lg border border-asgym-accent/30 bg-asgym-accent/10 text-asgym-accent transition-colors hover:bg-asgym-accent/20 disabled:opacity-40">
                          <ArchiveRestore size={16} />
                        </button>
                      )}
                      <button title={t('coach.delete')} disabled={isBusy} onClick={() => openDelete(c)} data-testid={`delete-btn-${c.id}`}
                        className="flex h-9 w-9 items-center justify-center rounded-lg border border-asgym-error/30 bg-asgym-error/10 text-asgym-error transition-colors hover:bg-asgym-error/20 disabled:opacity-40">
                        <Trash2 size={16} />
                      </button>
                      {view === 'active' && (
                        <button onClick={() => nav(`/coach/client/${c.id}`)} className="ml-1 text-asgym-muted" tabIndex={-1}>
                          <ChevronRight size={20} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Delete confirmation dialog */}
      <Dialog open={!!delTarget} onOpenChange={(o) => { if (!o && !deleting) { setDelTarget(null); setConfirmName(''); } }}>
        <DialogContent className="border-asgym-error/30 bg-asgym-surface text-white" data-testid="delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-asgym-error">
              <Trash2 size={18} />{t('coach.deleteTitle')}
            </DialogTitle>
            <DialogDescription className="text-asgym-sub">
              {t('coach.deleteWarning')}
            </DialogDescription>
          </DialogHeader>

          {delTarget && (
            <div className="space-y-3">
              <div className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-3">
                <p className="text-[11px] uppercase tracking-wide text-asgym-muted">{t('coach.deleteClient')}</p>
                <p className="mt-0.5 font-bold text-white">{fullName(delTarget)}</p>
                {delTarget.email && <p className="text-xs text-asgym-muted">{delTarget.email}</p>}
              </div>
              <div>
                <label className="text-xs text-asgym-sub">
                  {t('coach.deleteTypeName')} <span className="font-bold text-white">{fullName(delTarget)}</span>
                </label>
                <input
                  value={confirmName}
                  onChange={(e) => setConfirmName(e.target.value)}
                  data-testid="delete-confirm-input"
                  autoFocus
                  className="mt-1.5 h-11 w-full rounded-xl border border-white/[0.1] bg-asgym-bg px-3 text-sm text-white outline-none focus:border-asgym-error/60"
                  placeholder={fullName(delTarget)}
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-2">
            <button
              onClick={() => { if (!deleting) { setDelTarget(null); setConfirmName(''); } }}
              data-testid="delete-cancel-btn"
              className="h-11 flex-1 rounded-xl border border-white/[0.1] bg-white/[0.03] text-sm font-semibold text-white transition-colors hover:bg-white/[0.06]">
              {t('common.cancel')}
            </button>
            <button
              onClick={handleDelete}
              disabled={!confirmMatches || deleting}
              data-testid="delete-confirm-btn"
              className="h-11 flex-1 rounded-xl bg-asgym-error text-sm font-bold text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-30">
              {deleting ? t('common.loading') : t('coach.deletePermanently')}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </CoachShell>
  );
}
